/**
 * `/reach/ads` — Wend's Meta ads, on a page of their own. Slice 65.
 *
 * ## WHY ADS LEFT THE CAMPAIGNS PAGE
 *
 * Until slice 65 "Reach" meant two different jobs: messaging the people a
 * workspace holds (email and WhatsApp, from its own accounts, a receipt per
 * person) and buying attention from people it does not hold (Meta ads, on its
 * own ad account, figures that are Meta's aggregates). Slice 44 put the ads in
 * a panel under the campaigns table because "a Meta ad is a campaign on
 * another surface", and that was true of the word and nothing else: the table
 * and the panel shared no column, the search box had to be cut to the shorter
 * of two lists' limits, an ad surface in the filter hid the table and a status
 * hid the panel, and "No campaigns yet" had to be suppressed above a panel of
 * ads. Every one of those was a rule written to keep two pages on one screen.
 *
 * Slice 65 split the pillar: Reach is paid acquisition, Connect is owned
 * channels. The ads are Reach's, so they are here, and `/campaigns` is
 * Connect's alone again. **The rejected alternative was to keep the panel on
 * Campaigns** and only rename things — which would have left Connect's one
 * page carrying Reach's list, a sidebar group for Reach whose only link was a
 * panel on another pillar's screen, and every coupling above in place.
 *
 * ## THE INTEGRATION IS READ FIRST, AND THE DRAFTS ONLY BEHIND IT
 *
 * Slice 44's gate, kept: no Meta row means no ad route is called. The page
 * reads the integrations list — which answers on every database this app runs
 * against — and only a workspace with a Meta ad account connected mounts
 * {@link AdsList}, the one component here that calls the drafts route. The
 * gate is structural rather than a condition inside the read: an unconnected
 * workspace never mounts the list, so there is no render in which the drafts
 * route is asked and its answer thrown away. It spends none of the Meta read
 * limit and never touches a route that a database the Meta migrations have not
 * reached cannot answer.
 *
 * Unconnected, the page is one short card (`ReachAdsStates.tsx`, where a
 * suite can render it) linking to the Meta tab on Integrations — the place
 * the ad account is connected, kept there because Integrations holds every
 * account the workspace connects (Workspace, not a pillar). A failed read of the integration is an `ErrorState` with a retry,
 * as on every sibling page; the campaigns page used to draw that failure as
 * "no ad account", which was right for a guest panel and wrong for the page
 * whose whole subject is the ad account.
 *
 * ## THE LIST IS THE PANEL, NOT A FORK OF IT
 *
 * `MetaAdsPanel` and `MetaAdDraftList` draw the rows, the "Not read" chip,
 * "Show more" and the later-page failure exactly as they did on Campaigns;
 * this page fetches, as Campaigns did. The search and the surface filter run
 * on the server (slice 52), so they stay — the search capped at the drafts
 * route's own limit and refused on the box rather than truncated.
 *
 * The one state the panel cannot draw is a workspace with no ads at all: as a
 * guest it drew nothing then, because a placeholder for a surface a workspace
 * does not use is noise on someone else's page. Here the page *is* the ads, so
 * an empty list is the first-run state, drawn by `FirstAd` beside
 * `NoAdAccount`, and the panel is mounted only once there is something for it
 * to say.
 *
 * "New ad" is the page's one primary action, and only a connected workspace
 * has it — the composer it opens cannot compose without an account.
 */
import {
  DRAFTABLE_AD_SURFACES,
  META_AD_DRAFT_PAGE_DEFAULT,
  META_AD_DRAFT_SEARCH_MAX,
  SURFACE_ORDER,
  surfaceIsBuilt,
  surfaceSchema,
  type Surface,
} from '@wend/shared';
import {
  Card,
  CardContent,
  Input,
  PageHeader,
  Select,
  Spinner,
  buttonVariants,
  type SelectOption,
} from '@wend/ui';
import { Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { fetchMetaAdsIntegration, listMetaAdDrafts } from '../../lib/api/meta-ads';
import { useKeysetPages } from '../../lib/keyset-pages';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { surfaceLabel } from '../../lib/surface-display';
import { useAsyncData } from '../../lib/use-async-data';
import { MetaAdsPanel } from '../meta-ads/MetaAdDraftList';
import { FirstAd, NoAdAccount } from './ReachAdsStates';

/** Typing pauses this long before the list re-queries — the campaigns page's figure. */
const SEARCH_DEBOUNCE_MS = 300;

/** Where the search box's own refusal is announced from. */
const SEARCH_REFUSAL_ID = 'reach-ads-search-refusal';

/** "Every surface": Radix reserves `''` for the placeholder, so it needs a sentinel. */
const ANY_SURFACE = '__any__';

/**
 * The surfaces a draft can name, in taxonomy order — Facebook ads, Instagram
 * ads and, since slice 47, WhatsApp Status ads. Never a message surface: a
 * draft cannot name one, and the drafts route refuses it.
 */
const SURFACE_OPTIONS: readonly SelectOption[] = [
  { value: ANY_SURFACE, label: 'Any surface' },
  ...SURFACE_ORDER.filter(surfaceIsBuilt)
    .filter((surface) => DRAFTABLE_AD_SURFACES.includes(surface))
    .map((surface) => ({ value: surface, label: surfaceLabel(surface) })),
];

export function ReachAdsPage() {
  const integration = useAsyncData(async (signal) => ({ row: await fetchMetaAdsIntegration(signal) }), []);
  const connected = integration.data !== null && integration.data.row !== null;

  return (
    <AppLayout
      title="Meta ads"
      actions={
        connected ? (
          <Link to="/reach/ads/new" className={buttonVariants({ size: 'sm' })}>
            <Plus aria-hidden="true" className="size-4" />
            New ad
          </Link>
        ) : undefined
      }
    >
      <PageHeader
        eyebrow={PILLAR_IDENTITY.reach.label}
        icon={PILLAR_IDENTITY.reach.icon}
        lede="Paid ads on your own Meta ad account, drafted here and published paused."
      />

      {integration.data === null ? (
        <Card>
          {integration.loadingFirst ? (
            <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading…</span>
            </CardContent>
          ) : (
            <ErrorState
              error={integration.error}
              title="Could not load your Meta ads connection"
              onRetry={integration.reload}
            />
          )}
        </Card>
      ) : integration.data.row === null ? (
        <NoAdAccount />
      ) : (
        <AdsList />
      )}
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* The list — mounted only for a workspace with an ad account                  */
/* -------------------------------------------------------------------------- */

/**
 * The search, the surface filter and the drafts, read 25 at a time. The only
 * component on this page that calls a Meta ads route; the page mounts it only
 * behind a connected integration (see the header).
 */
function AdsList() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [surface, setSurface] = useState<Surface | null>(null);

  /*
    The drafts route takes a search of 80 characters. Longer is refused on the
    box that caused it rather than sent — a 400 for the list — or truncated,
    which would make the panel's "No match" a statement about a search nobody
    typed. Slice 52's rule, now that the box serves one list instead of two.
  */
  const tooLong = searchInput.trim().length > META_AD_DRAFT_SEARCH_MAX;

  useEffect(() => {
    if (tooLong) return;
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput, tooLong]);

  /*
    Paged and searched on the server since slice 52. `resetKey` is the whole
    query, so any change to the search or the surface drops the pages held
    during the same render rather than painting the old rows under the new
    search for a frame.
  */
  const ads = useKeysetPages(
    JSON.stringify([search, surface]),
    async (cursor, signal) =>
      listMetaAdDrafts(
        {
          limit: META_AD_DRAFT_PAGE_DEFAULT,
          ...(search === '' ? {} : { q: search }),
          ...(surface === null ? {} : { surface }),
          ...(cursor === null ? {} : { cursor }),
        },
        signal,
      ).then((page) => ({ rows: page.drafts, nextCursor: page.nextCursor })),
    [search, surface],
  );

  const filtered = search !== '' || surface !== null;
  const failed = ads.rows.length === 0 && ads.read.status === 'error';
  /* Nothing asked of the list and nothing in it: the first-run state, or the
     first read still in flight. A filter, a failure or a row hands the drawing
     to the panel, which owns every one of those states. */
  const unfilteredEmpty = !filtered && !failed && ads.rows.length === 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 sm:max-w-xs">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              className="pl-9"
              placeholder="Search by ad campaign name"
              aria-label="Search Meta ads"
              value={searchInput}
              aria-invalid={tooLong || undefined}
              aria-describedby={tooLong ? SEARCH_REFUSAL_ID : undefined}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
            />
          </div>
          {tooLong && (
            <p id={SEARCH_REFUSAL_ID} data-role="reach-ads-search-too-long" className="mt-1.5 text-[0.82rem] text-bad">
              Search for {META_AD_DRAFT_SEARCH_MAX} characters or fewer.
            </p>
          )}
        </div>

        <Select
          size="sm"
          className="w-auto min-w-[10rem]"
          aria-label="Filter by surface"
          options={SURFACE_OPTIONS}
          value={surface ?? ANY_SURFACE}
          onValueChange={(value) => {
            // The sentinel is not a surface, so a failed parse is "any surface";
            // a parsed surface a draft cannot name is treated the same way,
            // because the drafts route would refuse it.
            const parsed = surfaceSchema.safeParse(value);
            setSurface(parsed.success && DRAFTABLE_AD_SURFACES.includes(parsed.data) ? parsed.data : null);
          }}
        />

        {ads.read.refreshing && <Spinner size="sm" label="Updating the Meta ads" />}
      </div>

      {unfilteredEmpty ? (
        ads.read.status === 'loading' ? (
          <Card data-role="reach-ads-loading">
            <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading…</span>
            </CardContent>
          </Card>
        ) : (
          <FirstAd />
        )
      ) : (
        <MetaAdsPanel
          drafts={ads.rows}
          loading={ads.read.loadingFirst}
          loadingMore={ads.read.refreshing}
          failed={failed}
          failedMore={ads.failedMore}
          error={ads.read.error}
          onRetry={ads.restart}
          onMore={ads.hasMore ? ads.more : null}
          filtered={filtered}
          now={Date.now()}
        />
      )}
    </div>
  );
}
